// @ts-nocheck
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Cpu, 
  FileText, 
  Network, 
  Handshake,
  Database,
  CreditCard,
  Newspaper,
  Video,
  Share2,
  Users,
  GraduationCap,
  Building,
  Plus
} from "lucide-react";

interface PartnershipCategoriesProps {
  searchTerm: string;
}

const PARTNERSHIP_CATEGORIES = [
  {
    id: 'technology',
    title: 'Technology Integrations',
    description: 'API partners, data feeds, third-party tools, payment processors',
    icon: Cpu,
    color: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
    iconColor: 'text-blue-600',
    examples: [
      { name: 'Stripe Payment Processing', type: 'Payment Gateway', status: 'Active' },
      { name: 'Hudl Video Analysis', type: 'Video Platform', status: 'Integration' },
      { name: 'NCAA Eligibility API', type: 'Data Feed', status: 'Prospect' },
      { name: 'Zoom Video Calls', type: 'Communication', status: 'Active' }
    ]
  },
  {
    id: 'content',
    title: 'Content Partnerships',
    description: 'Sports media, recruiting publications, educational content, video platforms',
    icon: FileText,
    color: 'bg-purple-50 border-purple-200 hover:bg-purple-100',
    iconColor: 'text-purple-600',
    examples: [
      { name: 'ESPN Recruiting Network', type: 'Media Partner', status: 'Negotiation' },
      { name: 'MaxPreps Content', type: 'Sports Data', status: 'Active' },
      { name: 'Recruiting Academy', type: 'Educational', status: 'Prospect' },
      { name: 'Sports Illustrated', type: 'Publication', status: 'Discussion' }
    ]
  },
  {
    id: 'distribution',
    title: 'Distribution Partners',
    description: 'Marketing channels, affiliate networks, reseller programs, white label distributors',
    icon: Network,
    color: 'bg-green-50 border-green-200 hover:bg-green-100',
    iconColor: 'text-green-600',
    examples: [
      { name: 'Team Dealers Network', type: 'Reseller Program', status: 'Active' },
      { name: 'Coach Connection', type: 'Affiliate Network', status: 'Active' },
      { name: 'SchoolLinks Integration', type: 'White Label', status: 'Agreement' },
      { name: 'Athletic Directors Association', type: 'Channel Partner', status: 'Prospect' }
    ]
  },
  {
    id: 'strategic',
    title: 'Strategic Alliances',
    description: 'Sports organizations, coaching associations, educational institutions, industry leaders',
    icon: Handshake,
    color: 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100',
    iconColor: 'text-yellow-600',
    examples: [
      { name: 'National Coaches Association', type: 'Industry Organization', status: 'Active' },
      { name: 'Big Ten Conference', type: 'Sports Organization', status: 'Negotiation' },
      { name: 'Nike Elite Programs', type: 'Brand Partnership', status: 'Prospect' },
      { name: 'NAIA Partnership', type: 'Educational Institution', status: 'Active' }
    ]
  }
];

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'active': return 'bg-green-100 text-green-800';
    case 'negotiation': return 'bg-orange-100 text-orange-800';
    case 'agreement': return 'bg-blue-100 text-blue-800';
    case 'integration': return 'bg-purple-100 text-purple-800';
    case 'prospect': return 'bg-gray-100 text-gray-800';
    case 'discussion': return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const PartnershipCategories: React.FC<PartnershipCategoriesProps> = ({ searchTerm }) => {
  const filteredCategories = PARTNERSHIP_CATEGORIES.filter(category => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      category.title.toLowerCase().includes(searchLower) ||
      category.description.toLowerCase().includes(searchLower) ||
      category.examples.some(example => 
        example.name.toLowerCase().includes(searchLower) ||
        example.type.toLowerCase().includes(searchLower)
      )
    );
  });

  return (
    <div className="space-y-6">
      {/* Category Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {PARTNERSHIP_CATEGORIES.map(category => {
          const Icon = category.icon;
          const categoryCount = category.examples.length;
          const activeCount = category.examples.filter(e => e.status === 'Active').length;
          
          return (
            <Card key={category.id} className={`${category.color} transition-colors cursor-pointer`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-white ${category.iconColor}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">{category.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {activeCount}/{categoryCount} active
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detailed Category Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredCategories.map(category => {
          const Icon = category.icon;
          
          return (
            <Card key={category.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${category.iconColor} bg-muted`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">{category.title}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {category.description}
                    </p>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Partnership Examples */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-foreground">Current Partnerships</h4>
                    <Button variant="ghost" size="sm">
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {category.examples.map((example, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div>
                          <p className="font-medium text-foreground">{example.name}</p>
                          <p className="text-sm text-muted-foreground">{example.type}</p>
                        </div>
                        <Badge className={getStatusColor(example.status)}>
                          {example.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Category-specific Tools */}
                <div className="pt-4 border-t border-border">
                  <h4 className="font-medium text-foreground mb-3">Category Tools</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {category.id === 'technology' && (
                      <>
                        <Button variant="outline" size="sm" className="justify-start">
                          <Database className="h-3 w-3 mr-2" />
                          API Docs
                        </Button>
                        <Button variant="outline" size="sm" className="justify-start">
                          <CreditCard className="h-3 w-3 mr-2" />
                          Payments
                        </Button>
                      </>
                    )}
                    
                    {category.id === 'content' && (
                      <>
                        <Button variant="outline" size="sm" className="justify-start">
                          <Newspaper className="h-3 w-3 mr-2" />
                          Editorial
                        </Button>
                        <Button variant="outline" size="sm" className="justify-start">
                          <Video className="h-3 w-3 mr-2" />
                          Media
                        </Button>
                      </>
                    )}
                    
                    {category.id === 'distribution' && (
                      <>
                        <Button variant="outline" size="sm" className="justify-start">
                          <Share2 className="h-3 w-3 mr-2" />
                          Channels
                        </Button>
                        <Button variant="outline" size="sm" className="justify-start">
                          <Users className="h-3 w-3 mr-2" />
                          Affiliates
                        </Button>
                      </>
                    )}
                    
                    {category.id === 'strategic' && (
                      <>
                        <Button variant="outline" size="sm" className="justify-start">
                          <GraduationCap className="h-3 w-3 mr-2" />
                          Education
                        </Button>
                        <Button variant="outline" size="sm" className="justify-start">
                          <Building className="h-3 w-3 mr-2" />
                          Organizations
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredCategories.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <Handshake className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No partnership categories found</h3>
            <p className="text-muted-foreground">
              No categories match your search criteria.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PartnershipCategories;